import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Mail, Save, Send, Loader2, Clock, RefreshCw, UserPlus, Eye, History, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch (_) { return iso; }
};

const DailyDigestPanel = () => {
  const [cfg, setCfg] = useState({ enabled: true, hour: 8, minute: 0, timezone: "Europe/Athens" });
  const [info, setInfo] = useState({ recipients_monomeles: [], recipients_polymeles: [], next_run: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCourt, setPreviewCourt] = useState("Μονομελές");
  const [previewData, setPreviewData] = useState(null);

  // History
  const [history, setHistory] = useState([]);
  const [historySummary, setHistorySummary] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/admin/daily-digest/history`, { params: { limit: 100 } });
      setHistory(r.data?.items || []);
      setHistorySummary(r.data?.summary_30d || { sent: 0, failed: 0, skipped: 0 });
    } catch (e) { /* silent */ }
    finally { setHistoryLoading(false); }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/daily-digest/settings`);
      setCfg(r.data?.settings || cfg);
      setInfo({
        recipients_monomeles: r.data?.recipients_monomeles || [],
        recipients_polymeles: r.data?.recipients_polymeles || [],
        next_run: r.data?.next_run || "",
      });
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης ρυθμίσεων");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); fetchHistory(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/daily-digest/settings`, cfg);
      toast.success("Αποθηκεύτηκε. Νέα ώρα ισχύει από την επόμενη εκτέλεση.");
      fetchSettings();
    } catch (e) {
      toast.error("Σφάλμα αποθήκευσης");
    } finally { setSaving(false); }
  };

  const sendNow = async (ct = null) => {
    setSending(true);
    try {
      const url = `${API}/pretrial-tools/daily-digest/send-now` + (ct ? `?court_type=${encodeURIComponent(ct)}` : "");
      const r = await axios.post(url);
      const results = r.data?.results || [];
      results.forEach(res => {
        if (res.sent) toast.success(`${res.court_type}: ✉ στάλθηκε σε ${res.recipients?.length || 0}`);
        else toast.message(`${res.court_type}: ${res.reason || res.error || "—"}`);
      });
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής");
    } finally { setSending(false); }
  };

  const seedUsers = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/admin/seed-secretariat-users`);
      toast.success(r.data?.message || "Seed OK");
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setSeeding(false); }
  };

  const openPreview = async (ct) => {
    setPreviewCourt(ct);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const r = await axios.get(`${API}/pretrial-tools/daily-digest/preview`, { params: { court_type: ct } });
      setPreviewData(r.data);
    } catch (e) {
      toast.error("Σφάλμα preview");
    } finally { setPreviewLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />Καθημερινό Ενημερωτικό Email (Daily Digest)</CardTitle>
            <CardDescription>
              Αυτόματη αποστολή προς τις γραμματείες κάθε μέρα. Αναφορά: εκκρεμείς Πράξεις Ορισμού + στοιχεία δικαστών κοινοποίησης + Επίδοση Έως.
            </CardDescription>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchSettings} disabled={loading} data-testid="dd-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Ανανέωση
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Schedule */}
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr] gap-3 items-end">
          <div className="flex items-center gap-2">
            <Switch checked={cfg.enabled} onCheckedChange={v => setCfg({ ...cfg, enabled: v })} data-testid="dd-enabled" />
            <Label className="text-sm">{cfg.enabled ? "Ενεργό" : "Ανενεργό"}</Label>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Ώρα αποστολής</Label>
            <Input type="number" min="0" max="23" value={cfg.hour} onChange={e => setCfg({ ...cfg, hour: parseInt(e.target.value || "0", 10) })} data-testid="dd-hour" />
          </div>
          <div>
            <Label className="text-xs">Λεπτά</Label>
            <Input type="number" min="0" max="59" value={cfg.minute} onChange={e => setCfg({ ...cfg, minute: parseInt(e.target.value || "0", 10) })} data-testid="dd-minute" />
          </div>
          <div>
            <Label className="text-xs">Ζώνη Ώρας</Label>
            <Input value={cfg.timezone} onChange={e => setCfg({ ...cfg, timezone: e.target.value })} data-testid="dd-tz" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving} data-testid="dd-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Αποθήκευση Ρυθμίσεων
          </Button>
          <span className="text-xs text-slate-500">Επόμενη εκτέλεση: <strong>{cfg.hour.toString().padStart(2, "0")}:{cfg.minute.toString().padStart(2, "0")}</strong> {cfg.timezone}</span>
        </div>

        {/* Recipients */}
        <div className="border rounded p-3 bg-slate-50 space-y-2">
          <Label className="text-xs font-semibold">Παραλήπτες (από χρήστες με ρόλο Γραμματείας)</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <div>
              <strong className="text-blue-700">Μονομελές</strong>
              <div className="mt-1 flex flex-wrap gap-1">
                {info.recipients_monomeles.length === 0 ? <span className="text-slate-400 italic">— καμία διεύθυνση —</span>
                  : info.recipients_monomeles.map((e, i) => <Badge key={i} variant="outline" className="text-[10px]">{e}</Badge>)}
              </div>
            </div>
            <div>
              <strong className="text-purple-700">Πολυμελές</strong>
              <div className="mt-1 flex flex-wrap gap-1">
                {info.recipients_polymeles.length === 0 ? <span className="text-slate-400 italic">— καμία διεύθυνση —</span>
                  : info.recipients_polymeles.map((e, i) => <Badge key={i} variant="outline" className="text-[10px]">{e}</Badge>)}
              </div>
            </div>
          </div>
          <p className="text-[10px] text-slate-500 italic">
            Για να προστεθούν παραλήπτες, δημιουργήστε χρήστη στο Admin Panel με ρόλο «Γραμματεία Προδικαστικού Μον/Πολ» ή «Συντονιστής».
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap border-t pt-3">
          <Button variant="outline" size="sm" onClick={() => openPreview("Μονομελές")} data-testid="dd-preview-mon">
            <Eye className="w-4 h-4 mr-1" /> Preview Μονομελούς
          </Button>
          <Button variant="outline" size="sm" onClick={() => openPreview("Πολυμελές")} data-testid="dd-preview-pol">
            <Eye className="w-4 h-4 mr-1" /> Preview Πολυμελούς
          </Button>
          <div className="flex-1" />
          <Button size="sm" onClick={() => sendNow(null)} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dd-send-now">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Αποστολή Τώρα (Μον.+Πολ.)
          </Button>
          <Button size="sm" variant="outline" onClick={seedUsers} disabled={seeding} data-testid="dd-seed">
            {seeding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Seed Default Χρήστες Γραμματείας
          </Button>
        </div>

        {/* HISTORY SECTION */}
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4" />Ιστορικό Αποστολών</h3>
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />{historySummary.sent} επιτυχείς</Badge>
              <Badge className="bg-red-600 text-white"><XCircle className="w-3 h-3 mr-1" />{historySummary.failed} σφάλματα</Badge>
              <Badge variant="outline"><MinusCircle className="w-3 h-3 mr-1" />{historySummary.skipped} παράλειψη</Badge>
              <span className="text-slate-400">(30 ημερών)</span>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={fetchHistory} disabled={historyLoading} data-testid="dd-history-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${historyLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto border rounded max-h-[360px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 sticky top-0">
                  <th className="p-2 text-left">Ημ/νία & Ώρα</th>
                  <th className="p-2 text-left">Δικαστήριο</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-center">Trigger</th>
                  <th className="p-2 text-center">Παραλήπτες</th>
                  <th className="p-2 text-center">Πράξεις</th>
                  <th className="p-2 text-left">Λεπτομέρειες</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-slate-400 italic">Καμία αποστολή ακόμη</td></tr>
                ) : history.map((h, idx) => (
                  <tr key={h.id || idx} className="border-t hover:bg-slate-50" data-testid={`dd-hist-${idx}`}>
                    <td className="p-2 font-mono">{fmtDT(h.started_at)}</td>
                    <td className="p-2">
                      <Badge variant="outline" className={`text-[10px] ${h.court_type === "Πολυμελές" ? "border-purple-300 text-purple-700" : "border-blue-300 text-blue-700"}`}>
                        {h.court_type}
                      </Badge>
                    </td>
                    <td className="p-2 text-center">
                      {h.sent
                        ? <Badge className="bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" />Στάλθηκε</Badge>
                        : h.error
                          ? <Badge className="bg-red-600 text-white text-[10px]"><XCircle className="w-3 h-3 mr-0.5" />Σφάλμα</Badge>
                          : <Badge variant="outline" className="text-[10px]"><MinusCircle className="w-3 h-3 mr-0.5" />{h.reason || "—"}</Badge>}
                    </td>
                    <td className="p-2 text-center">
                      <Badge variant="outline" className="text-[9px]">
                        {h.trigger === "manual" ? `Χειροκίνητο${h.triggered_by ? ` (${h.triggered_by})` : ""}` : "Auto"}
                      </Badge>
                    </td>
                    <td className="p-2 text-center font-mono">{h.recipients_count || 0}</td>
                    <td className="p-2 text-center font-mono">{h.pending_acts_count || 0}</td>
                    <td className="p-2 text-xs text-slate-500 max-w-[280px] truncate" title={h.error || h.reason || (h.recipients || []).join(", ")}>
                      {h.error ? <span className="text-red-600">{h.error}</span>
                        : h.reason ? h.reason
                        : (h.recipients || []).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview Digest — {previewCourt}</DialogTitle>
            <DialogDescription>
              Προεπισκόπηση του ενημερωτικού email για τη γραμματεία πριν την αποστολή.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : previewData ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">Παραλήπτες: {(previewData.recipients || []).join(", ") || "—"}</Badge>
                {previewData.has_data
                  ? <Badge className="bg-emerald-600 text-white">Έχει δεδομένα</Badge>
                  : <Badge variant="outline" className="border-amber-300 text-amber-700">Δεν υπάρχουν εκκρεμότητες</Badge>}
              </div>
              {previewData.has_data ? (
                <>
                  <div className="border rounded p-2 bg-slate-50">
                    <strong>Subject:</strong> <span className="font-mono text-xs">{previewData.subject}</span>
                  </div>
                  <pre className="border rounded p-3 bg-white max-h-[400px] overflow-auto text-xs whitespace-pre-wrap font-mono">{previewData.body}</pre>
                </>
              ) : (
                <p className="text-slate-500 italic">Όλες οι παραγμένες Πράξεις Ορισμού έχουν ήδη κοινοποιηθεί. Το digest δεν θα σταλεί.</p>
              )}
            </div>
          ) : (
            <p className="text-slate-400">—</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Κλείσιμο</Button>
            {previewData?.has_data && (
              <Button onClick={() => { setPreviewOpen(false); sendNow(previewCourt); }} className="bg-emerald-600 hover:bg-emerald-700">
                <Send className="w-4 h-4 mr-1" /> Αποστολή Τώρα ({previewCourt})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DailyDigestPanel;
